/**
 * Speed drill: the boards, the modes, and the scoring.
 *
 * A drill board is a real puzzle — one solution, every region at least two squares — answered by
 * clicking a square one of its queens stands on, rather than by solving it out. A board you can't
 * read can be skipped instead of guessed at: another takes its place, and the only cost is the
 * seconds already spent on it. A wrong answer costs those seconds plus a three-second penalty. The
 * session clock carrying all of that is the score.
 *
 * Where the boards come from depends on the size, because the two sizes are nothing alike:
 *   - **4×4** has exactly two legal placements and 7,216 drillable boards, so the pool is
 *     enumerated whole and dealt as a shuffled deck (no repeats, both answers equally often).
 *   - **5×5** has 14 legal placements and roughly 14 million boards — far too many to enumerate,
 *     so they are generated on demand: grow regions out from a random placement's queens and keep
 *     the result only if it is a proper puzzle a glance can crack (see `BANNED_RULES`). About 8%
 *     of candidates survive, ~1.7ms per board kept.
 */

import { solve } from './solver';
import { buildGraph } from '../shared/analysis/graph';
import { RuleId } from '../shared/rules';

const LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

/** No region may be smaller than this: a lone square announces its own queen. */
const MIN_REGION = 2;

/**
 * Boards you have to get **right** to finish a session. Neither a miss nor a skip counts against
 * it — another board takes the place of either — so every session ends the same way, five found,
 * and the only thing that separates two of them is the clock.
 */
export const SESSION_LENGTH = 5;

/**
 * Added to the session clock for each wrong answer.
 *
 * A miss ends its board instantly, so without a penalty the quickest way through a session would
 * be to click at random until something landed. Three seconds is worth more than a guess and less
 * than giving up on a board you can actually read.
 */
export const WRONG_PENALTY_MS = 3_000;

export interface Cell { row: number; col: number }

/**
 * How long after a click the same square stops answering.
 *
 * The real game takes two clicks to place a queen (blank → ✕ → queen), so a player arrives here
 * with a double-tap in their fingers. Here one click answers and the next board is up instantly,
 * which puts that second tap on a *new* board — almost always a wrong square, and a 3s penalty for
 * a habit rather than a mistake. Long enough to swallow the tail of a double-tap, short enough
 * that a deliberate second look at the same square still lands.
 */
export const ECHO_TAP_MS = 500;

/** Is this click the tail of a double-tap — the same square, still inside the window? */
export function isEchoTap(last: { cell: Cell; at: number } | null, cell: Cell, now: number): boolean {
  if (!last) return false;
  if (last.cell.row !== cell.row || last.cell.col !== cell.col) return false;
  return now - last.at < ECHO_TAP_MS;
}

/**
 * The only two queen placements a 4×4 admits (one per row and column, no diagonal touching),
 * as cols[row] = col. Mirror images of each other, and cell-disjoint: on a 4×4 a single square
 * names the whole placement.
 */
export const CONFIGS: readonly (readonly number[])[] = [
  [1, 3, 0, 2],
  [2, 0, 3, 1],
];

/** Every legal placement for a board of this size, as cols[row] = col. Memoized per size. */
const placementCache = new Map<number, number[][]>();
export function legalPlacements(size: number): number[][] {
  const hit = placementCache.get(size);
  if (hit) return hit;

  const out: number[][] = [];
  const cols: number[] = [];
  (function walk(row: number, usedCols: number) {
    if (row === size) { out.push([...cols]); return; }
    for (let col = 0; col < size; col++) {
      if (usedCols & (1 << col)) continue;
      if (row > 0 && Math.abs(col - cols[row - 1]) === 1) continue;
      cols.push(col);
      walk(row + 1, usedCols | (1 << col));
      cols.pop();
    }
  })(0, 0);

  placementCache.set(size, out);
  return out;
}

// ---- The 4×4 pool ----

const SIZE4 = 4;
const CELLS4 = SIZE4 * SIZE4;
const FULL4 = (1 << CELLS4) - 1;

/** Orthogonal neighbors of each 4×4 cell, as a bitmask. */
const NBR4 = (() => {
  const nbr = new Int32Array(CELLS4);
  for (let i = 0; i < CELLS4; i++) {
    const r = (i / SIZE4) | 0, c = i % SIZE4;
    let m = 0;
    if (r > 0) m |= 1 << (i - SIZE4);
    if (r < SIZE4 - 1) m |= 1 << (i + SIZE4);
    if (c > 0) m |= 1 << (i - 1);
    if (c < SIZE4 - 1) m |= 1 << (i + 1);
    nbr[i] = m;
  }
  return nbr;
})();

const CONFIG_MASKS = CONFIGS.map(cols => cols.reduce((m, col, row) => m | (1 << (row * SIZE4 + col)), 0));

function popcount(x: number): number {
  x = x - ((x >> 1) & 0x55555555);
  x = (x & 0x33333333) + ((x >> 2) & 0x33333333);
  x = (x + (x >> 4)) & 0x0f0f0f0f;
  return (x * 0x01010101) >> 24;
}

/**
 * Every connected subset of `allowed` that contains `seed`, each produced exactly once.
 *
 * Grows the piece one frontier cell at a time, and bans a cell from every later branch once its
 * "include it" branch has been explored — that is what keeps a subset from being reached by more
 * than one growth order.
 */
function eachConnected(seed: number, allowed: number, cb: (piece: number) => void) {
  const seedBit = 1 << seed;
  const grow = (sub: number, ext: number, forb: number) => {
    cb(sub);
    let rest = ext;
    while (rest) {
      const v = rest & -rest;
      rest &= ~v;
      const i = 31 - Math.clz32(v);
      grow(sub | v, rest | (NBR4[i] & allowed & ~sub & ~v & ~forb), forb);
      forb |= v;
    }
  };
  grow(seedBit, NBR4[seed] & allowed & ~seedBit, 0);
}

export interface PoolBoard {
  /** Region labels in row-major order ("ABBA…"), canonical: regions are lettered by first cell. */
  code: string;
  /** Index into `CONFIGS` — the single placement this board's regions allow. */
  answer: 0 | 1;
}

/**
 * Partitions of the 4×4 into 4 connected regions of 2+ squares that leave exactly one placement
 * standing.
 *
 * A region holding zero or two-plus of a placement's queens rules that placement out, so the walk
 * carries the set still alive and abandons a branch the moment it empties — which is most of them
 * (only 13,073 of 62,741 partitions are solvable at all). Single-square regions are dropped at the
 * same point, before they cost a subtree.
 */
function enumerate4(): PoolBoard[] {
  const boards: PoolBoard[] = [];
  const pieces: number[] = [];

  const walk = (remaining: number, partsLeft: number, alive: number) => {
    if (remaining === 0) {
      if (partsLeft !== 0 || popcount(alive) !== 1) return;
      const cells = new Array<string>(CELLS4);
      pieces.forEach((piece, k) => {
        for (let i = 0; i < CELLS4; i++) if (piece & (1 << i)) cells[i] = LABELS[k];
      });
      boards.push({ code: cells.join(''), answer: alive === 1 ? 0 : 1 });
      return;
    }
    if (partsLeft === 0) return;
    // Seeding each piece at the lowest unassigned cell both fixes a canonical region order (so
    // every partition is produced once, not 4! times) and makes the labels deterministic.
    const seed = 31 - Math.clz32(remaining & -remaining);
    eachConnected(seed, remaining, piece => {
      if (popcount(piece) < MIN_REGION) return;
      let next = alive;
      for (let k = 0; k < CONFIG_MASKS.length; k++) {
        if (popcount(piece & CONFIG_MASKS[k]) !== 1) next &= ~(1 << k);
      }
      if (next === 0) return;
      pieces.push(piece);
      walk(remaining & ~piece, partsLeft - 1, next);
      pieces.pop();
    });
  };

  walk(FULL4, SIZE4, (1 << CONFIG_MASKS.length) - 1);
  return boards;
}

let pool: PoolBoard[] | null = null;

/** Every drillable 4×4, built once per page load (~30ms). */
export function allBoards(): PoolBoard[] {
  if (!pool) pool = enumerate4();
  return pool;
}

export function regionsFromCode(code: string, size: number): string[][] {
  const rows: string[][] = [];
  for (let r = 0; r < size; r++) rows.push(code.slice(r * size, r * size + size).split(''));
  return rows;
}

// ---- Generated boards (5×5 and up) ----

/**
 * Grow one region out from each of a placement's queens until the board is covered.
 *
 * Repeatedly takes a random unclaimed square that touches a claimed one and joins it to a random
 * claimed neighbor's region, which keeps every region connected by construction and gives regions
 * that look hand-drawn rather than striped.
 */
function growRegions(size: number, placement: number[], rng: () => number): string[][] {
  const grid: (string | null)[][] = Array.from({ length: size }, () => Array(size).fill(null));
  placement.forEach((col, row) => { grid[row][col] = LABELS[row]; });

  const neighbors = (r: number, c: number) =>
    ([[r - 1, c], [r + 1, c], [r, c - 1], [r, c + 1]] as Array<[number, number]>)
      .filter(([a, b]) => a >= 0 && b >= 0 && a < size && b < size);

  for (let left = size * size - size; left > 0; left--) {
    const frontier: Array<{ r: number; c: number; from: string[] }> = [];
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        if (grid[r][c]) continue;
        const from = neighbors(r, c).map(([a, b]) => grid[a][b]).filter((x): x is string => x !== null);
        if (from.length) frontier.push({ r, c, from });
      }
    }
    const pick = frontier[Math.floor(rng() * frontier.length)];
    grid[pick.r][pick.c] = pick.from[Math.floor(rng() * pick.from.length)];
  }

  return grid as string[][];
}

function smallestRegion(regions: string[][]): number {
  const counts = new Map<string, number>();
  for (const row of regions) for (const cell of row) counts.set(cell, (counts.get(cell) ?? 0) + 1);
  return Math.min(...counts.values());
}

/**
 * Techniques a drill board must never *require*. The drill is read at a glance, and both of these
 * are worked out rather than seen — a line squeeze needs the candidates of a row narrowed and their
 * joint reach traced, a paired shadow needs that done across two units at once. A board is kept
 * only if it can be solved without either, so what the drill trains is spotting, not grinding.
 */
const BANNED_RULES: ReadonlySet<RuleId> = new Set<RuleId>(['R2', 'R9']);

/** Can this board be solved without the techniques the drill keeps out? (~0.8ms at 5×5.) */
function readableAtAGlance(regions: string[][]): boolean {
  return buildGraph(regions, {
    disable: BANNED_RULES,
    supportHorizon: 0,
    minimizePremises: false, // solvability is all that's asked here; the premise sets go unread
  }).solved;
}

/**
 * A generated board that is a genuine puzzle: one solution, no region under two squares, and no
 * need for a banned technique.
 *
 * Rejection sampling — about 8% of grown candidates survive (a quarter of the failures are a
 * one-square region, the rest are boards a second placement also satisfies), which costs well
 * under a millisecond per board. The technique check runs only on what's left of them, and turns
 * away another ~5%. Sizes below 5 have too few placements for this to terminate usefully; they use
 * the enumerated pool instead.
 */
export function generateBoard(size: number, rng: () => number = Math.random): Deal {
  const placements = legalPlacements(size);
  for (;;) {
    const solution = placements[Math.floor(rng() * placements.length)];
    const regions = growRegions(size, solution, rng);
    if (smallestRegion(regions) < MIN_REGION) continue;
    // Cap at 2: all we ever need to know is whether a second solution exists.
    if (solve(regions, 2).length !== 1) continue;
    if (!readableAtAGlance(regions)) continue;
    return { code: regions.flat().join(''), regions: recolor(regions, rng), solution: [...solution] };
  }
}

/**
 * The same board painted with a random permutation of the region labels.
 *
 * Recoloring is not a new puzzle, but it does stop the eye from learning a board by its palette
 * ("the red one is a Left") instead of by its shape.
 */
export function recolor(regions: string[][], rng: () => number = Math.random): string[][] {
  const used = [...new Set(regions.flat())].sort();
  const perm = used.map((_, i) => LABELS[i]);
  for (let i = perm.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [perm[i], perm[j]] = [perm[j], perm[i]];
  }
  return regions.map(row => row.map(cell => perm[used.indexOf(cell)]));
}

// ---- Modes ----

export type DrillModeId = 'q4' | 'q5' | 'q4unlimited' | 'q5uniform';

/**
 * A drill version. Answering is identical everywhere — one tap, no ✕s; what differs is the shape
 * of a run: find `SESSION_LENGTH` boards against a clock that counts up, or answer as many as you
 * can before a clock counts down.
 */
export interface DrillMode {
  id: DrillModeId;
  /** Short label for the picker. */
  label: string;
  size: number;
  /**
   * Length of the countdown, for the sprint versions. Absent means a find-five session.
   *
   * The two shapes score in opposite directions — a session is won by a small clock, a sprint by a
   * big board count — which is why `bestRuns` takes the mode rather than assuming lower is better.
   */
  limitMs?: number;
  /**
   * Deal from `DECK_URL` instead of growing boards on the spot.
   *
   * The generator is not uniform over the boards it could deal — a board's chance is proportional
   * to how many growth orders reach it, which favours compact, balanced regions heavily enough
   * that the pool it *effectively* draws from is a small fraction of the real one. The deck is a
   * genuinely uniform sample, taken offline by walking the whole space
   * ([scripts/build-uniform-drill.ts](scripts/build-uniform-drill.ts)), so this version is the
   * same drill over a different distribution of boards — which is the point of having both.
   */
  deck?: boolean;
}

export const MODES: readonly DrillMode[] = [
  { id: 'q4', label: '4×4', size: 4 },
  { id: 'q5', label: '5×5', size: 5 },
  { id: 'q5uniform', label: '5×5 uniform', size: 5, deck: true },
  { id: 'q4unlimited', label: '4×4 unlimited', size: 4, limitMs: 20_000 },
];

/** A countdown run rather than a find-five session. */
export function isSprint(mode: DrillMode): boolean {
  return mode.limitMs !== undefined;
}

export const DEFAULT_MODE: DrillModeId = 'q4';

export function modeById(id: string | null | undefined): DrillMode {
  return MODES.find(m => m.id === id) ?? MODES.find(m => m.id === DEFAULT_MODE)!;
}

// ---- Dealing ----

export interface Deal {
  /** Region labels row-major — the log's key, and what the review modal redraws. */
  code: string;
  regions: string[][];
  /** cols[row] = col. The board's one solution. */
  solution: number[];
}

/** A board of a prebuilt deck: its region code, and which placement solves it. */
export interface DeckBoard {
  code: string;
  /** Index into `legalPlacements(size)` — the board's one solution. */
  answer: number;
}

/** Built by [scripts/build-uniform-drill.ts](scripts/build-uniform-drill.ts); rebuild it there. */
export const DECK_URL = '/drill-uniform-5.txt';

export function parseDeck(text: string): DeckBoard[] {
  const out: DeckBoard[] = [];
  for (const line of text.split('\n')) {
    if (!line) continue;
    const [code, answer] = line.split(' ');
    out.push({ code, answer: Number(answer) });
  }
  return out;
}

let deckRequest: Promise<DeckBoard[]> | null = null;

/**
 * The uniform deck, fetched once per page load and kept.
 *
 * Not bundled: it is 20,000 boards, and only one version of the drill ever asks for it. A failed
 * fetch is not cached, so opening the version again retries rather than staying broken.
 */
export function loadDeck(url: string = DECK_URL): Promise<DeckBoard[]> {
  if (!deckRequest) {
    deckRequest = fetch(url)
      .then(r => {
        if (!r.ok) throw new Error(`deck ${r.status}`);
        return r.text();
      })
      .then(parseDeck)
      .catch(err => { deckRequest = null; throw err; });
  }
  return deckRequest;
}

/**
 * Deals boards for a mode: a shuffled pass over a pool — the enumerated 4×4s, or the uniform 5×5
 * deck — or freshly generated boards at 5×5.
 *
 * A pool is dealt as a deck rather than sampled: no board twice until the pool is gone, which is
 * both fairer practice and the only way a session can't ask the same board twice.
 */
export function createDealer(
  mode: DrillMode,
  rng: () => number = Math.random,
  deck?: DeckBoard[],
): () => Deal {
  const boards: readonly DeckBoard[] | null =
    mode.deck ? (deck ?? null) : mode.size === SIZE4 ? allBoards() : null;
  if (!boards) {
    // A deck version with no deck loaded would deal nothing at all; growing boards is wrong for it
    // but beats a dead board, and `DrillView` doesn't start a session until the fetch lands.
    return () => generateBoard(mode.size, rng);
  }

  const placements = mode.size === SIZE4 ? CONFIGS : legalPlacements(mode.size);
  let order: number[] = [];
  return () => {
    if (order.length === 0) {
      order = boards.map((_, i) => i);
      for (let i = order.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
      }
    }
    const board = boards[order.pop()!];
    return {
      code: board.code,
      regions: recolor(regionsFromCode(board.code, mode.size), rng),
      solution: [...placements[board.answer]],
    };
  };
}

// ---- Judging a click ----

/** Is this square one of the board's queens? Anything else ends the board. */
export function isWanted(solution: number[], cell: Cell): boolean {
  return solution[cell.row] === cell.col;
}

/** The board's queens as cells — what the review modal draws. */
export function solutionCells(solution: number[]): Cell[] {
  return solution.map((col, row) => ({ row, col }));
}

// ---- Scoring ----

export interface Attempt {
  code: string;
  /** The colors it was shown in, so the log and the review modal match what was on screen. */
  regions: string[][];
  solution: number[];
  mode: DrillModeId;
  /** Correct queens placed, in click order — on a win, all of them. */
  placed: Cell[];
  /** The click that ended the board, or null when it was answered without a miss. */
  miss: Cell | null;
  /** Passed on rather than guessed at. Never correct, but not a wrong answer either. */
  skipped: boolean;
  correct: boolean;
  ms: number;
}

export interface DrillStats {
  /** Boards seen, skips included. */
  attempts: number;
  /** Boards found. Five of these ends the session — nothing else does. */
  correct: number;
  skipped: number;
  wrong: number;
  /** Boards answered: `attempts` less the skips. */
  answered: number;
  /** Time actually spent, every board seen included. */
  totalMs: number;
  /** `WRONG_PENALTY_MS` per miss. */
  penaltyMs: number;
  /**
   * The session clock, and the score: time spent plus penalties. Skipping costs only the seconds
   * already gone into the board — otherwise it would be free, and the fastest way through would be
   * to skip until an easy board turned up.
   */
  clockMs: number;
  /** Share of answered boards that were right, 0–1. Skips are not wrong answers. */
  accuracy: number;
  /** Mean over answered boards, right or wrong. */
  avgMs: number;
  /** Mean over correct answers only — the number that actually tracks reading speed. */
  avgCorrectMs: number;
  /** Fastest correct answer. */
  bestMs: number | null;
  streak: number;
  bestStreak: number;
}

export const EMPTY_STATS: DrillStats = {
  attempts: 0, correct: 0, skipped: 0, wrong: 0, answered: 0, totalMs: 0, penaltyMs: 0, clockMs: 0,
  accuracy: 0, avgMs: 0, avgCorrectMs: 0, bestMs: null, streak: 0, bestStreak: 0,
};

/** `attempts` newest-first, the order the log keeps them in. */
export function summarize(attempts: Attempt[]): DrillStats {
  if (attempts.length === 0) return EMPTY_STATS;

  const totalMs = attempts.reduce((sum, a) => sum + a.ms, 0);
  // Everything but the clock is about answers, so a skip drops out here: it neither counts
  // against accuracy nor breaks a run.
  const answers = attempts.filter(a => !a.skipped);

  let correct = 0, answeredMs = 0, correctMs = 0, bestMs: number | null = null;
  for (const a of answers) {
    answeredMs += a.ms;
    if (a.correct) {
      correct++;
      correctMs += a.ms;
      if (bestMs === null || a.ms < bestMs) bestMs = a.ms;
    }
  }

  // Newest-first, so the current streak is the run at the head.
  let streak = 0;
  while (streak < answers.length && answers[streak].correct) streak++;

  let bestStreak = 0, run = 0;
  for (const a of answers) {
    run = a.correct ? run + 1 : 0;
    if (run > bestStreak) bestStreak = run;
  }

  const wrong = answers.length - correct;
  const penaltyMs = wrong * WRONG_PENALTY_MS;

  return {
    attempts: attempts.length,
    correct,
    skipped: attempts.length - answers.length,
    wrong,
    answered: answers.length,
    totalMs,
    penaltyMs,
    clockMs: totalMs + penaltyMs,
    accuracy: answers.length > 0 ? correct / answers.length : 0,
    avgMs: answers.length > 0 ? answeredMs / answers.length : 0,
    avgCorrectMs: correct > 0 ? correctMs / correct : 0,
    bestMs,
    streak,
    bestStreak,
  };
}

// ---- Lifetime totals ----

/**
 * Per mode: a 4×4 one-queen time and a 5×5 two-queen time are not the same measurement, so they
 * do not share a record. The default mode keeps the original key, from before modes existed.
 */
function storageKey(mode: DrillModeId): string {
  return mode === DEFAULT_MODE ? 'drillTotals' : `drillTotals:${mode}`;
}

/** What survives a reload: totals only, never the log itself. */
export interface DrillTotals {
  attempts: number;
  correct: number;
  skipped: number;
  correctMs: number;
  bestMs: number | null;
  bestStreak: number;
  /** Sessions played to the end. */
  sessions: number;
  /**
   * Fastest session clock. Every session is the same five found, and misses are paid for on the
   * clock, so one number compares two runs outright.
   */
  bestTimeMs: number | null;
}

export const EMPTY_TOTALS: DrillTotals = {
  attempts: 0, correct: 0, skipped: 0, correctMs: 0, bestMs: null, bestStreak: 0, sessions: 0,
  bestTimeMs: null,
};

export function loadTotals(mode: DrillModeId): DrillTotals {
  try {
    const raw = localStorage.getItem(storageKey(mode));
    if (!raw) return EMPTY_TOTALS;
    const parsed = JSON.parse(raw) as Partial<DrillTotals>;
    return {
      attempts: Number(parsed.attempts) || 0,
      correct: Number(parsed.correct) || 0,
      skipped: Number(parsed.skipped) || 0,
      correctMs: Number(parsed.correctMs) || 0,
      bestMs: typeof parsed.bestMs === 'number' ? parsed.bestMs : null,
      bestStreak: Number(parsed.bestStreak) || 0,
      // Absent from records written before sessions and skips existed; 0 reads correctly for both.
      sessions: Number(parsed.sessions) || 0,
      bestTimeMs: typeof parsed.bestTimeMs === 'number' ? parsed.bestTimeMs : null,
    };
  } catch {
    return EMPTY_TOTALS; // unreadable or unavailable storage is just an empty record, never a crash
  }
}

export function saveTotals(mode: DrillModeId, totals: DrillTotals): void {
  try {
    localStorage.setItem(storageKey(mode), JSON.stringify(totals));
  } catch {
    /* private mode / full quota: lifetime totals are a nicety, the session still works */
  }
}

/**
 * Booked once a run is finished, so an abandoned half-run doesn't count.
 *
 * `clockMs` is null for a sprint: its record is a board count, which the server-side history keeps
 * — there is no local best worth a field of its own.
 */
export function finishSession(totals: DrillTotals, clockMs: number | null): DrillTotals {
  return {
    ...totals,
    sessions: totals.sessions + 1,
    bestTimeMs: clockMs !== null && (totals.bestTimeMs === null || clockMs < totals.bestTimeMs)
      ? clockMs
      : totals.bestTimeMs,
  };
}

export function addAttempt(totals: DrillTotals, attempt: Attempt, streak: number): DrillTotals {
  return {
    ...totals,
    attempts: totals.attempts + 1,
    correct: totals.correct + (attempt.correct ? 1 : 0),
    skipped: totals.skipped + (attempt.skipped ? 1 : 0),
    correctMs: totals.correctMs + (attempt.correct ? attempt.ms : 0),
    bestMs: attempt.correct && (totals.bestMs === null || attempt.ms < totals.bestMs) ? attempt.ms : totals.bestMs,
    bestStreak: Math.max(totals.bestStreak, streak),
  };
}

// ---- Session history ----

/**
 * One board as it is recorded. The regions are kept as they were *shown* (recolored), because the
 * palette was randomised per board and which colors sat next to each other may well move the read
 * time; `ms` is the time on that board alone, which is the measurement the whole record exists
 * for. The picks are kept because where a queen went is the interesting part of a board someone
 * got wrong.
 */
export interface DrillBoardRecord {
  /** Region labels row-major, as displayed. */
  cells: string;
  size: number;
  /** cols[row] = col — the board's one solution. */
  solution: number[];
  /** Squares a queen was put on and accepted, in order. */
  placed: Cell[];
  /** The square that ended it, when that was a wrong queen. */
  miss: Cell | null;
  outcome: 'found' | 'missed' | 'skipped';
  /** Time on this board alone. */
  ms: number;
}

/** What the client sends when a session finishes. */
export interface DrillSessionPayload {
  playerToken: string;
  mode: DrillModeId;
  /** When the session finished, by the client's clock. */
  at: number;
  /** The score: time spent plus penalties. */
  clockMs: number;
  totalMs: number;
  penaltyMs: number;
  found: number;
  missed: number;
  skipped: number;
  /** Every board of the session, in the order they were dealt. */
  boards: DrillBoardRecord[];
}

/** A recorded session as the server lists it back — metadata only; boards are fetched per session. */
export interface DrillSessionSummary {
  id: number;
  mode: string;
  at: number;
  createdAt: number;
  clockMs: number;
  totalMs: number;
  penaltyMs: number;
  found: number;
  missed: number;
  skipped: number;
  boardCount: number;
}

export function toBoardRecord(attempt: Attempt): DrillBoardRecord {
  return {
    cells: attempt.regions.flat().join(''),
    size: attempt.regions.length,
    solution: attempt.solution,
    placed: attempt.placed,
    miss: attempt.miss,
    outcome: attempt.correct ? 'found' : attempt.skipped ? 'skipped' : 'missed',
    ms: attempt.ms,
  };
}

/**
 * Sessions that haven't reached the server yet.
 *
 * The record is the point of the feature, so a session is not dropped because a request failed —
 * it waits in localStorage and goes up with the next attempt. This is a send buffer, not a copy
 * of the history: entries leave it as soon as the server has them.
 */
const PENDING_KEY = 'drillPending';
const PENDING_CAP = 50;

function readPending(): DrillSessionPayload[] {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as DrillSessionPayload[]).filter(p => p && Array.isArray(p.boards)) : [];
  } catch {
    return [];
  }
}

function writePending(list: DrillSessionPayload[]): void {
  try {
    if (list.length === 0) localStorage.removeItem(PENDING_KEY);
    else localStorage.setItem(PENDING_KEY, JSON.stringify(list.slice(-PENDING_CAP)));
  } catch {
    /* private mode / full quota: the send buffer is best-effort */
  }
}

export function queuePending(payload: DrillSessionPayload): void {
  writePending([...readPending(), payload]);
}

export function pendingCount(): number {
  return readPending().length;
}

/**
 * Moves any history left in the browser by the version that kept it there into the send queue.
 *
 * One-time and idempotent — the key is removed once its sessions are queued — so nobody's earlier
 * runs are lost to the move onto the server.
 */
export function migrateLocalHistory(playerToken: string): void {
  try {
    const raw = localStorage.getItem('drillHistory');
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      // Stored newest-first; queue oldest-first so they upload in the order they were played.
      for (const old of [...parsed].reverse()) {
        if (!old || !Array.isArray(old.boards)) continue;
        queuePending({ ...(old as Omit<DrillSessionPayload, 'playerToken'>), playerToken });
      }
    }
    localStorage.removeItem('drillHistory');
  } catch {
    /* unreadable: nothing to rescue */
  }
}

/**
 * Sends everything waiting, oldest first, and stops at the first failure so order is kept and a
 * server that is down doesn't turn into a burst of doomed requests.
 *
 * Returns how many are still waiting.
 */
export async function flushPending(send: (p: DrillSessionPayload) => Promise<unknown>): Promise<number> {
  let queue = readPending();
  while (queue.length > 0) {
    try {
      await send(queue[0]);
    } catch {
      break;
    }
    queue = queue.slice(1);
    writePending(queue);
  }
  return queue.length;
}

// ---- Records ----

/**
 * The drill's two headline numbers — a session's best clock, or a sprint's best board count.
 * Which one, and which direction is better, is the mode's business.
 */
export interface DrillRecords {
  /** Best run finished today, by the player's own calendar day. */
  today: number | null;
  allTime: number | null;
}

export const NO_RECORDS: DrillRecords = { today: null, allTime: null };

function sameDay(a: number, b: number): boolean {
  const x = new Date(a), y = new Date(b);
  return x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth() && x.getDate() === y.getDate();
}

/** What a finished run is worth: a sprint is judged on boards found, a session on its clock. */
export function runScore(mode: DrillMode, run: { clockMs: number; found: number }): number {
  return isSprint(mode) ? run.found : run.clockMs;
}

function beats(mode: DrillMode, score: number, record: number | null): boolean {
  if (record === null) return true;
  return isSprint(mode) ? score > record : score < record;
}

/**
 * All a run needs to be scored against the records: a recorded session qualifies, and so does one
 * that has only just finished and hasn't come back from the server yet.
 */
export type ScoredRun = Pick<DrillSessionSummary, 'mode' | 'at' | 'clockMs' | 'found'>;

/**
 * The best run, today and ever, for one version.
 *
 * Versions are kept apart because a 4×4 clock, a 5×5 clock and a sprint's board count are not the
 * same measurement, and "today" is the *player's* day — `at` is their clock, which is the one
 * their idea of today comes from, not the server's.
 */
export function bestRuns(
  sessions: ScoredRun[],
  mode: DrillMode,
  now: number = Date.now(),
): DrillRecords {
  let today: number | null = null, allTime: number | null = null;
  for (const s of sessions) {
    if (s.mode !== mode.id) continue;
    const score = runScore(mode, s);
    if (beats(mode, score, allTime)) allTime = score;
    if (sameDay(s.at, now) && beats(mode, score, today)) today = score;
  }
  return { today, allTime };
}

// ---- The trend ----

/** Sessions behind the trend line. Ten runs is a couple of sittings — enough to average out a
 *  board that would not read, short enough to still move within a week. */
export const TREND_WINDOW = 10;

/**
 * The trailing mean of the last `window` values, one per value.
 *
 * A single session says almost nothing — one board that took ten seconds to read swings its clock
 * further than a week of practice does — so the plot's story is this line rather than the points
 * under it. The first values average what there is so far rather than being left out: a player is
 * owed a line from their third session, not their tenth.
 */
export function rollingAverage(values: number[], window: number = TREND_WINDOW): number[] {
  const out: number[] = [];
  let sum = 0;
  for (let i = 0; i < values.length; i++) {
    sum += values[i];
    if (i >= window) sum -= values[i - window];
    out.push(sum / Math.min(i + 1, window));
  }
  return out;
}

/** One version's runs oldest first — the order they were played, which is what a trend is over. */
export function inPlayOrder(sessions: DrillSessionSummary[]): DrillSessionSummary[] {
  // By `at` and not by id: a session held in the send queue uploads after runs played later.
  return [...sessions].sort((a, b) => a.at - b.at);
}

/** Seconds to 2dp — at drill speeds "00:01" says nothing useful. */
export function formatMs(ms: number): string {
  return `${(ms / 1000).toFixed(2)}s`;
}
